/**
 * ContactBlock — phone / email with Call · Text · Email buttons, and inline
 * editing of the candidate's name, email and phone (`PATCH /api/leads/:id/candidate`).
 * A 409 `phone_in_use` is shown under the phone field, not as a toast.
 */
import { useId, useState } from 'react';
import { Mail, MessageSquare, Pencil, Phone } from 'lucide-react';
import { ApiError } from '@/lib/api-client';
import { errorMessage, useToast } from '@/components/Toast';
import type { Candidate } from '@/features/pipeline/types';
import { useLeadProfileMutations, type CandidatePatch } from './lead-api';

const inputClass =
  'mt-1 block min-h-10 w-full rounded-md border border-gray-300 bg-white px-2.5 text-sm text-gray-900 aria-[invalid=true]:border-danger';

function ContactLink({ href, icon: Icon, label }: { href: string; icon: typeof Phone; label: string }) {
  return (
    <a
      href={href}
      className="inline-flex min-h-10 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-3 text-[13px] font-medium text-navy hover:border-navy/40 hover:bg-page"
    >
      <Icon size={14} strokeWidth={2} aria-hidden /> {label}
    </a>
  );
}

export function ContactBlock({ leadId, candidate }: { leadId: string; candidate: Candidate }) {
  const { candidate: save } = useLeadProfileMutations(leadId);
  const toast = useToast();
  const id = useId();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Required<CandidatePatch>>({ first_name: '', last_name: '', email: '', phone: '' });
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const start = () => {
    setForm({
      first_name: candidate.first_name ?? '',
      last_name: candidate.last_name ?? '',
      email: candidate.email ?? '',
      phone: candidate.phone_e164 ?? '',
    });
    setPhoneError(null);
    setFormError(null);
    setEditing(true);
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const patch: CandidatePatch = {};
    const first = form.first_name.trim();
    const last = form.last_name.trim();
    const email = form.email?.trim() ?? '';
    const phone = form.phone.trim();
    if (first !== (candidate.first_name ?? '')) patch.first_name = first;
    if (last !== (candidate.last_name ?? '')) patch.last_name = last;
    if (email !== (candidate.email ?? '')) patch.email = email || null;
    if (phone !== (candidate.phone_e164 ?? '')) patch.phone = phone;
    if (Object.keys(patch).length === 0) return setEditing(false);
    setPhoneError(null);
    setFormError(null);
    save.mutate(patch, {
      onSuccess: () => {
        setEditing(false);
        toast.push({ kind: 'success', title: 'Contact details saved' });
      },
      onError: (err) => {
        if (err instanceof ApiError && (err.code === 'phone_in_use' || err.status === 409)) {
          setPhoneError('Another candidate in your company already has this phone number.');
        } else {
          setFormError(errorMessage(err, 'Could not save. Try again.'));
        }
      },
    });
  };

  if (!editing) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {candidate.phone_e164 && (
          <>
            <ContactLink href={`tel:${candidate.phone_e164}`} icon={Phone} label="Call" />
            <ContactLink href={`sms:${candidate.phone_e164}`} icon={MessageSquare} label="Text" />
          </>
        )}
        {candidate.email && <ContactLink href={`mailto:${candidate.email}`} icon={Mail} label="Email" />}
        <button
          type="button"
          onClick={start}
          className="inline-flex min-h-10 items-center gap-1.5 rounded-md px-2.5 text-[13px] font-medium text-muted hover:bg-page hover:text-gray-900"
        >
          <Pencil size={14} strokeWidth={2} aria-hidden /> Edit contact
        </button>
      </div>
    );
  }

  const field = (key: keyof Required<CandidatePatch>, label: string, type = 'text', error?: string | null) => (
    <label className="block text-[13px]" htmlFor={`${id}-${key}`}>
      <span className="font-medium text-gray-800">{label}</span>
      <input
        id={`${id}-${key}`}
        type={type}
        value={form[key] ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-${key}-err` : undefined}
        required={key === 'first_name' || key === 'phone'}
        className={inputClass}
      />
      {error && (
        <span id={`${id}-${key}-err`} role="alert" className="mt-1 block text-[12px] text-danger">
          {error}
        </span>
      )}
    </label>
  );

  return (
    <form onSubmit={submit} className="rounded-lg border border-navy/30 bg-white p-3" aria-label="Edit contact details">
      <div className="grid gap-3 sm:grid-cols-2">
        {field('first_name', 'First name')}
        {field('last_name', 'Last name')}
        {field('phone', 'Phone', 'tel', phoneError)}
        {field('email', 'Email', 'email')}
      </div>
      {formError && (
        <p role="alert" className="mt-2 text-[12px] text-danger">
          {formError}
        </p>
      )}
      <div className="mt-3 flex gap-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="inline-flex min-h-10 items-center rounded-md bg-navy px-3.5 text-sm font-medium text-white hover:bg-navy-dark disabled:opacity-50"
        >
          {save.isPending ? 'Saving…' : 'Save contact'}
        </button>
        <button
          type="button"
          onClick={() => setEditing(false)}
          className="inline-flex min-h-10 items-center rounded-md px-3 text-sm font-medium text-gray-700 hover:bg-page"
        >
          Cancel
        </button>
      </div>
    </form>
  );
}
